import { useEffect, useState } from "react";
import {
  Title2,
  Body1,
  Spinner,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableBody,
  TableCell,
  makeStyles,
  tokens
} from "@fluentui/react-components";

interface AdminDashboard {
  id: string;
  displayName: string;
  quickDashboardId: string;
  awsRegion: string;
  isActive: boolean;
}

interface AdminClient {
  id: string;
  displayName: string;
  entraGroupId: string;
  isActive: boolean;
  dashboards: AdminDashboard[];
}

interface ManageClientsResponse {
  clients: AdminClient[];
}

const useStyles = makeStyles({
  intro: { marginBottom: tokens.spacingVerticalL, color: tokens.colorNeutralForeground3 },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 },
  inactive: { color: tokens.colorPaletteRedForeground1 },
  active: { color: tokens.colorPaletteGreenForeground1 }
});

/**
 * Internal-only admin overview. Read-only list of every client and its
 * dashboards with the config that drives entitlement + embedding. Adding or
 * editing clients/dashboards is still done via SQL migrations (repo
 * convention); this page is for verifying and debugging what's configured.
 */
export default function Admin() {
  const styles = useStyles();
  const [data, setData] = useState<ManageClientsResponse | "loading" | "error">("loading");

  useEffect(() => {
    fetch("/api/manage-clients")
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as ManageClientsResponse;
      })
      .then((d) => setData(d))
      .catch(() => setData("error"));
  }, []);

  if (data === "loading") return <Spinner size="medium" label="Loading clients…" />;
  if (data === "error") {
    return <Body1>Couldn't load clients. The database may be waking up — try refreshing in about a minute.</Body1>;
  }

  return (
    <div>
      <Title2>Clients &amp; dashboards</Title2>
      <Body1 className={styles.intro} as="p">
        Read-only overview of configured clients and their dashboards. Add or edit
        via SQL migration (see <span className={styles.mono}>docs/client-onboarding.md</span>).
      </Body1>
      <Table size="small" aria-label="Clients and dashboards">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Client</TableHeaderCell>
            <TableHeaderCell>Entra group ID</TableHeaderCell>
            <TableHeaderCell>Dashboard</TableHeaderCell>
            <TableHeaderCell>QuickSight dashboard ID</TableHeaderCell>
            <TableHeaderCell>Region</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.clients.flatMap((c) =>
            c.dashboards.length === 0
              ? [
                  <TableRow key={c.id}>
                    <TableCell>{c.displayName}</TableCell>
                    <TableCell className={styles.mono}>{c.entraGroupId}</TableCell>
                    <TableCell colSpan={3}>
                      <Body1 className={styles.inactive}>no dashboards</Body1>
                    </TableCell>
                    <TableCell className={c.isActive ? styles.active : styles.inactive}>
                      {c.isActive ? "Active" : "Inactive"}
                    </TableCell>
                  </TableRow>
                ]
              : c.dashboards.map((d, i) => {
                  const live = c.isActive && d.isActive;
                  return (
                    <TableRow key={`${c.id}/${d.id}`}>
                      <TableCell>{i === 0 ? c.displayName : ""}</TableCell>
                      <TableCell className={styles.mono}>{i === 0 ? c.entraGroupId : ""}</TableCell>
                      <TableCell>{d.displayName}</TableCell>
                      <TableCell className={styles.mono}>{d.quickDashboardId}</TableCell>
                      <TableCell>{d.awsRegion}</TableCell>
                      <TableCell className={live ? styles.active : styles.inactive}>
                        {live ? "Active" : c.isActive ? "Dashboard off" : "Client off"}
                      </TableCell>
                    </TableRow>
                  );
                })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
